// components/Reader/HighlightableText.tsx
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import parse from "html-react-parser";
import {
  applyHighlightsToContainer,
  getRangeIndexesRelativeToContainer,
  removeMarks,
} from "../../utils/DOMUtils";
import HighlightPopup from "./HighlightPopup";
import { useHighlight } from "../../../context";
import { usePublication } from "../../../hooks/usePublication";

interface HighlightableTextProps {
  htmlContent: string;
  fragmentId: string;
  className?: string;
}

const HighlightableText = React.memo(({ 
  htmlContent, 
  fragmentId, 
  className = "p-container gcontent" 
}: HighlightableTextProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastMouseDownInsidePopupRef = useRef(false);
  const {publication} = usePublication()
  const [popupInfo, setPopupInfo] = useState<{
    rect: DOMRect | null;
    selectionInfo?: {
      fragmentId: string;
      text: string;
      startIndex: number;
      endIndex: number;
      chapterId: string;
    } | null;
    existingHighlight?: any | null;
  } | null>(null);

  const { highlights } = useHighlight();

  // Aplicar estilos y limpiar HTML similar a TXT.tsx
  const processedHtml = htmlContent
    .replaceAll("<table", "<table class='table'")
    .replaceAll("<th>", "<th class='table__th'>")
    .replaceAll("<button", "<button class='reference' ")
    .replaceAll(
      "</table>",
      "<tfoot><tr><td colspan='100'></td></tr></tfoot></table>"
    );

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const fragmentHighlights = highlights.filter(
      (h) => h.fragmentId === fragmentId
    );

    if (fragmentHighlights.length === 0) {
      removeMarks(container);
      return;
    }

    applyHighlightsToContainer(
      container,
      fragmentHighlights.map((h) => ({
        id: h.id,
        startIndex: h.startIndex,
        endIndex: h.endIndex,
        color: h.color,
        note: h.note,
      }))
    );
  }, [processedHtml, highlights, fragmentId]);

  const handleMouseUp = useCallback(() => {
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const range = sel.getRangeAt(0);

      const container = containerRef.current;
      if (!container) return;
      if (
        !container.contains(range.startContainer) &&
        !container.contains(range.endContainer)
      )
        return;

      const rect = range.getBoundingClientRect();
      if (!rect || rect.width === 0 || rect.height === 0) return;

      const { start, end } = getRangeIndexesRelativeToContainer(
        container,
        range
      );
      if (start === end) return;
      const title = publication.sections?.[publication.currentSection]?.title || 'Desconocido';

      setPopupInfo({
        rect,
        selectionInfo: {
          fragmentId: fragmentId,
          text: range.toString(),
          startIndex: start,
          endIndex: end,
          chapterId: title,
        },
      });
    }, 20);
  }, [fragmentId]);

  const handleClick = useCallback(
    (ev: React.MouseEvent) => {
      const target = ev.target as HTMLElement;
      const mark =
        (target.closest &&
          (target.closest(
            "mark[data-hl-id], .highlight-note-icon[data-hl-id]"
          ) as HTMLElement | null)) ||
        ((ev.nativeEvent as any).composedPath &&
          (ev.nativeEvent as any)
            .composedPath()
            .find(
              (n: any) =>
                n &&
                n.nodeType === 1 &&
                typeof (n as Element).matches === "function" &&
                (n as Element).matches("mark[data-hl-id]")
            ));
      const el = (mark as HTMLElement) || null;
      if (!el) return;
      const id = el.dataset?.hlId;
      if (!id) return;
      const highlight = highlights.find((h) => h.id === id);
      if (!highlight) return;
      const rect = el.getBoundingClientRect();
      setPopupInfo({ rect, existingHighlight: highlight });
    },
    [highlights]
  );

  // Efectos para manejar el cierre del popup (igual que en TXT.tsx)
  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!popupInfo) {
        lastMouseDownInsidePopupRef.current = false;
        return;
      }

      const path: EventTarget[] = (e as any).composedPath
        ? (e as any).composedPath()
        : [];
      let clickedInsidePopup = false;

      if (path && path.length) {
        for (const node of path) {
          if (node && typeof (node as Element).hasAttribute === "function") {
            try {
              if ((node as Element).hasAttribute("data-hl-popup")) {
                clickedInsidePopup = true;
                break;
              }
            } catch {
              /* ignore */
            }
          }
        }
      }

      if (!clickedInsidePopup) {
        const tgt = e.target as Element | null;
        if (tgt) {
          const closestPopup = tgt.closest && tgt.closest("[data-hl-popup]");
          if (closestPopup) clickedInsidePopup = true;
        }
      }

      lastMouseDownInsidePopupRef.current = clickedInsidePopup;

      const container = containerRef.current;
      const clickedInsideContainer =
        container instanceof HTMLElement && e.target instanceof Node
          ? container.contains(e.target as Node)
          : false;

      if (!clickedInsidePopup && !clickedInsideContainer) {
        setPopupInfo(null);
      }
    }

    function onSelectionChange() {
      if (!popupInfo) return;

      if (lastMouseDownInsidePopupRef.current) return;

      const active = document.activeElement;
      if (
        active instanceof HTMLElement &&
        (active.closest("[data-hl-popup]") ||
          (active.hasAttribute && active.hasAttribute("data-hl-popup")))
      ) {
        return;
      }

      const sel = window.getSelection();
      if (!sel) return;
      if (sel.isCollapsed) {
        setPopupInfo(null);
      }
    }

    document.addEventListener("mousedown", onDocMouseDown, true);
    document.addEventListener("selectionchange", onSelectionChange);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown, true);
      document.removeEventListener("selectionchange", onSelectionChange);
    };
  }, [popupInfo]);

  return (
    <>
      <div
        ref={containerRef}
        className={className}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
      >
        {parse(processedHtml)}
      </div>

      {popupInfo && (
        <HighlightPopup
          selectionInfo={popupInfo.selectionInfo}
          existingHighlight={popupInfo.existingHighlight}
          onClose={() => setPopupInfo(null)}
        />
      )}
    </>
  );
});

export default HighlightableText;